// "On standby" dashboard block (Session ANY). Who is on call this week and next, on the launchpad.
//
// WHY IT IS ON THE DASHBOARD and not only on the rota page: the rota page is where the arrangement
// is SET, which happens once a quarter; this is the question that gets asked at six on a Friday, by
// whoever is picking up the phone. Two weeks and nothing else -- this week because it is the answer,
// next week because "who is on after me" is the only follow-up anyone ever asks.
//
// Self-fetching with its own loading and error states, like every other registered block. A failure
// here says so plainly rather than rendering an empty card: an on-call panel that silently shows
// nobody is worse than one that admits it could not load.
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { StandbyOverview, StandbyWeek } from "../../lib/api";
import { SkeletonRows } from "../../components/Skeleton";

/** How one week reads on the panel. Pure, so the four states test without a render. */
export function standbyPanelLine(week: StandbyWeek | undefined): { name: string; note: string } {
  if (!week) return { name: "Nobody on standby", note: "No rota covers this week." };
  if (week.engineerId && week.engineerName) {
    return {
      name: week.engineerName,
      note: week.source === "override" ? (week.reason ? `Set for this week. ${week.reason}` : "Set for this week.") : "From the rota.",
    };
  }
  if (week.source === "override") return { name: "Nobody on standby", note: week.reason ? `Set for this week. ${week.reason}` : "Set for this week." };
  return { name: "Nobody on standby", note: "No rota covers this week." };
}

function WeekLine({ label, week }: { label: string; week: StandbyWeek | undefined }): ReactNode {
  const line = standbyPanelLine(week);
  return (
    <div>
      <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">{label}</p>
      <p className="mt-0.5 font-heading text-xl tracking-wide text-ele-strong">{line.name}</p>
      <p className="mt-0.5 text-xs text-ele-body">{line.note}</p>
    </div>
  );
}

export function StandbyPanel(): ReactNode {
  const [data, setData] = useState<StandbyOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    api.standby
      // Two weeks is the whole panel; asking for the page's default twelve would fetch ten rows
      // nobody renders.
      .overview({ weeks: 2 })
      .then((d) => {
        if (live) setData(d);
      })
      .catch(() => {
        if (live) setError(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [reloadKey]);

  return (
    <section className="mb-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="ele-section-title">On standby</h2>
        <Link to="/office/standby" className="ele-view-all">
          The standby rota &#8594;
        </Link>
      </div>

      {loading ? (
        <div role="status" aria-live="polite">
          <span className="sr-only">Loading the standby rota...</span>
          <SkeletonRows rows={2} />
        </div>
      ) : error ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-center">
          <p className="text-ele-strong">Could not load who is on standby.</p>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:text-ele-strong"
          >
            Retry
          </button>
        </div>
      ) : data ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <WeekLine label="This week" week={data.weeks[0]} />
          <WeekLine label="Next week" week={data.weeks[1]} />
        </div>
      ) : null}
    </section>
  );
}
